/**
 * employeeInfoService.js
 * Data source for the Employee Info page (ESS -> Employee Info).
 *
 * Pulls one employee's full profile from the HRMS controllers that expose a
 * by-employee lookup. Every call goes through the shared HRMS client
 * (src/api/api.js -> hrmsGet), so they automatically carry the signed-in
 * user's Bearer token, X-Tenant-Code and X-Service-Code from sessionDB.
 *
 * Service codes below are taken from the login `enabledServices` tree
 * (HRMS Team -> Employees -> Onboarding). Each section is fetched
 * independently and a failure is confined to its own card, so an endpoint
 * whose path shape is wrong degrades to "Not available" rather than taking
 * the page down.
 */

import { hrmsGet, hrmsPost, hrmsPut } from "../../../api/api";
import sessionDB from "../../../utils/sessionDB";
import { ADDRESS_FIELDS, DATE_OF_BIRTH_FIELDS, PHONE_FIELDS, CONTACT_TYPE_FIELDS, EMERGENCY_FIELDS } from "./profileFields";

// path builders — (empId) => request path (relative to /saas/hrms)
const ENDPOINTS = {
  master: (id) => `/emp-info/search/${id}`,
  emergency: (id) => `/emergency-info/search/${id}`,
  education: (id) => `/emp-education/search/${id}`,
  employment: (id) => `/current-employment/search/${id}`,
  // Paged + sorted: newest posting first, capped at the most recent 10.
  shiftMapping: (id) =>
    `/emp-shift-mapping/search/employee?empId=${id}&page=0&size=10&sortBy=effectiveFrom&sortDir=DESC`,
  documents: (id) => `/emp-documents/search/${id}`,
  positionHistory: (id) => `/position-history/search/${id}`,
  previousEmployment: (id) => `/emp-previous-employment/search/emp/${id}`,
  assetAllocation: (id) => `/emp-asset-allocations/employee/${id}`,
  accessCard: (id) => `/access-card/search/employee/${id}`,
  accountDetails: (id) => `/account-details/search/${id}`,
};

const SERVICE_CODES = {
  master: "HR01S", // Search All Employees
  emergency: "HR06S", // Search Emergency Info List
  education: "HR09S", // Search Education Records List
  employment: "HR02S", // Search Current Employment Criteria
  shiftMapping: "HR25S", // Search Employee Shifts Mapping
  documents: "HR04S", // Search Employee Document By ID
  positionHistory: "HR03S", // Search Position History Criteria
  previousEmployment: "HR10S", // Search Previous Employment List
  assetAllocation: "HR08S", // Search Asset Allocations List
  accessCard: "HR07S", // Search Access Card List
  accountDetails: "HR05S", // Search Account Details By ID
};

/**
 * Sections that come back as zero-or-more records and render as a repeated
 * block of fields. Everything else on the page is a single record.
 */
const LIST_SECTIONS = [
  "emergency",
  "education",
  "shiftMapping",
  "documents",
  "positionHistory",
  "previousEmployment",
  "assetAllocation",
  "accessCard",
  "accountDetails",
];

/** Unwrap the standard `{ message, data, timestamp, ... }` envelope. */
function unwrap(body) {
  return body?.data ?? body ?? null;
}

/** Normalise a list-shaped payload (array, or {content:[...]}) to an array. */
function asList(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.content)) return data.content;
  if (data && typeof data === "object") return [data]; // single record -> 1-item list
  return [];
}

function fetchSection(section, empId) {
  return hrmsGet(ENDPOINTS[section](encodeURIComponent(empId)), SERVICE_CODES[section]).then(unwrap).catch((error) => {
    // A missing emergency record can be created. Permission/network/route failures remain errors.
    if (section === "emergency" && error.status === 404 && /emergency info(?:rmation)? not found/i.test(error.message)) return null;
    throw error;
  });
}

/**
 * emp_master / getEmployeeById.
 * Returns the raw record (firstName, lastName, fullName, email, phone,
 * contactType, address*, hasEmpPic, …); the page reads its fields directly.
 */
export function fetchEmployeeMaster(empId) {
  return fetchSection("master", empId);
}

/** emp_current_employment / getEmployment — current employment record. */
export function fetchCurrentEmployment(empId) {
  return fetchSection("employment", empId);
}

/**
 * Loads every section for `empId` in parallel and NEVER rejects except on the
 * master record: each section resolves to either its data or an `{ error }`
 * marker, so one unreachable endpoint only blanks its own card. Master is a
 * hard error because without it there is nothing meaningful to show.
 */
export async function fetchEmployeeInfo(empId) {
  const [master, employment, ...lists] = await Promise.allSettled([
    fetchEmployeeMaster(empId),
    fetchCurrentEmployment(empId),
    ...LIST_SECTIONS.map((section) => fetchSection(section, empId).then(asList)),
  ]);

  if (master.status === "rejected") {
    throw new Error(master.reason?.message || "Failed to load employee details.");
  }

  const reason = (r) => (r.status === "rejected" ? r.reason?.message || "Failed to load" : null);

  const sections = {};
  const errors = { employment: reason(employment) };
  LIST_SECTIONS.forEach((section, i) => {
    sections[section] = lists[i].status === "fulfilled" ? lists[i].value : [];
    errors[section] = reason(lists[i]);
  });

  return {
    master: master.value,
    employment: employment.status === "fulfilled" ? employment.value : null,
    ...sections,
    errors,
  };
}

/** Keep ESS writes scoped to the signed-in employee, even in Team search mode. */
function requireOwnEmployee(empId) {
  const ownId = String(sessionDB.getEmpId() ?? "").trim();
  if (!/^[1-9][0-9]*$/.test(ownId) || String(empId) !== ownId) {
    throw new Error("You can only edit your own employee profile.");
  }
  return encodeURIComponent(ownId);
}

function editablePayload(fields, updates) {
  return Object.fromEntries(fields
    .filter(({ key }) => Object.prototype.hasOwnProperty.call(updates, key))
    .map(({ key }) => [key, updates[key]]));
}

export async function saveEmployeeProfile(empId, updates) {
  const id = requireOwnEmployee(empId);
  const payload = editablePayload([...DATE_OF_BIRTH_FIELDS, ...PHONE_FIELDS, ...CONTACT_TYPE_FIELDS, ...ADDRESS_FIELDS, { key: "empPicBase64" }], updates);
  return unwrap(await hrmsPut("/emp-info/update/" + id, payload, "HR01U"));
}

export async function saveEmergencyContact(empId, updates, creating = false) {
  const id = requireOwnEmployee(empId);
  const payload = editablePayload(EMERGENCY_FIELDS, updates);
  return unwrap(await (creating
    ? hrmsPost("/emergency-info/add", { ...payload, empId: Number(id) }, "HR06A")
    : hrmsPut("/emergency-info/update/" + id, payload, "HR06U")));
}
